import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { customDashboardApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  FileText, CheckCircle, TrendingUp, Clock, Loader2, LayoutDashboard
} from 'lucide-react';

export default function HomepageDashboardTab() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await customDashboardApi.homepage();
        setDashboard(res.data.dashboard);
      } catch { /* no homepage set */ }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-[#0E7490] animate-spin" /></div>;
  if (!dashboard) return null;

  const color = dashboard.color || '#0E7490';

  return (
    <div className="space-y-4" data-testid="homepage-dashboard-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-6 rounded-full" style={{ backgroundColor: color }} />
          <h2 className="text-lg font-semibold text-[#0A2342]">{dashboard.name}</h2>
        </div>
        <Link to={`/dashboards/${dashboard.id}`}>
          <Button size="sm" variant="outline" className="text-xs" data-testid="view-full-dashboard"><LayoutDashboard className="w-3 h-3 mr-1" />View Full</Button>
        </Link>
      </div>

      {/* Compact stats */}
      <div className="grid grid-cols-3 gap-3">
        <MiniStat icon={FileText} value={dashboard.stats?.total_forms || 0} label="Forms" color={color} />
        <MiniStat icon={CheckCircle} value={dashboard.stats?.total_submissions || 0} label="Submissions" color="#166534" />
        <MiniStat icon={TrendingUp} value={dashboard.stats?.total_forms > 0 ? Math.round((dashboard.stats?.total_submissions || 0) / dashboard.stats.total_forms) : 0} label="Avg/Form" color="#7C3AED" />
      </div>

      {/* Compact form cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        {(dashboard.forms || []).slice(0, 6).map((form) => (
          <Card key={form.id} className="careiq-card overflow-hidden" data-testid={`hp-form-${form.id}`}>
            <div className="h-1" style={{ backgroundColor: form.category_color || '#64748B' }} />
            <CardContent className="p-3">
              <div className="flex items-start justify-between mb-1">
                <h4 className="text-xs font-semibold text-[#0A2342] truncate flex-1">{form.name}</h4>
                <span className="text-sm font-bold ml-2" style={{ color: form.category_color || color }}>{form.submissions_count}</span>
              </div>
              {form.category && (
                <span className="text-[8px] px-1 py-0.5 rounded text-white" style={{ backgroundColor: form.category_color || '#64748B' }}>{form.category}</span>
              )}
              <div className="mt-2">
                <Link to={`/forms/${form.id}/fill`}>
                  <Button size="sm" className="w-full h-7 text-[10px] text-white" style={{ backgroundColor: form.category_color || color }}>
                    Fill Form
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, value, label, color }) {
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: color + '08' }}>
      <Icon className="w-4 h-4" style={{ color }} />
      <div>
        <div className="text-lg font-bold text-[#0A2342]">{value}</div>
        <div className="text-[10px] text-[#64748B]">{label}</div>
      </div>
    </div>
  );
}
